import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, DatePicker, Select, Input, Tag, Tooltip, Modal,
  Typography, Row, Col, Space, Button, Statistic, message as antMessage,
} from 'antd';
import { ReloadOutlined, SendOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchMessageLogs, fetchMessageLogById } from '../services/messageLogsApi';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { value: 'sent',      label: 'Enviado',  color: 'default' },
  { value: 'delivered', label: 'Entregue', color: 'blue' },
  { value: 'read',      label: 'Lido',     color: 'green' },
  { value: 'failed',    label: 'Falhou',   color: 'red' },
];

const statusMeta = (s) =>
  STATUS_OPTIONS.find((o) => o.value === s) || { label: s, color: 'default' };

const MessageLogs = () => {
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const [type, setType] = useState(null);
  const [status, setStatus] = useState(null);

  // Inputs (typing) vs debounced committed values (query params).
  const [customerNameInput, setCustomerNameInput] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [cellphoneInput, setCellphoneInput] = useState('');
  const [cellphone, setCellphone] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [teamsCount, setTeamsCount] = useState(0);
  const [playersCount, setPlayersCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [types, setTypes] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setCustomerName(customerNameInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [customerNameInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setCellphone(cellphoneInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [cellphoneInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: range[0].format('YYYY-MM-DD'),
        date_to: range[1].format('YYYY-MM-DD'),
        page,
        page_size: pageSize,
      };
      if (customerName) params.customer_name = customerName;
      if (cellphone)    params.cellphone = cellphone;
      if (type)         params.type = type;
      if (status)       params.status = status;

      const data = await fetchMessageLogs(params);
      setItems(data.items);
      setTotal(data.total);
      setTeamsCount(data.teams_count ?? 0);
      setPlayersCount(data.players_count ?? 0);

      const uniqueTypes = Array.from(new Set(data.items.map((i) => i.type).filter(Boolean)));
      setTypes((prev) => Array.from(new Set([...prev, ...uniqueTypes])).sort());
    } catch (e) {
      antMessage.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  }, [range, customerName, cellphone, type, status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await fetchMessageLogById(record.id);
      setDetail(data);
    } catch {
      antMessage.error('Erro ao carregar detalhe');
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: 'Data/Hora',
      dataIndex: 'created_on',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm:ss') : '-',
    },
    { title: 'Time',     dataIndex: 'customer_name', width: 180 },
    { title: 'Jogador',  dataIndex: 'player_name',   width: 160 },
    { title: 'Telefone', dataIndex: 'cellphone',     width: 140 },
    { title: 'Tipo',     dataIndex: 'type',          width: 120 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (s, row) => {
        const meta = statusMeta(s);
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return row.error_message
          ? <Tooltip title={row.error_message}>{tag}</Tooltip>
          : tag;
      },
    },
    {
      title: 'Atualizado',
      dataIndex: 'status_updated_at',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm:ss') : '-',
    },
  ];

  return (
    <div>
      <Title level={3}>Logs de Envio</Title>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Mensagens enviadas"
              value={total}
              prefix={<SendOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic
              title="Times"
              value={teamsCount}
              prefix={<TeamOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic
              title="Jogadores"
              value={playersCount}
              prefix={<UserOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={7}>
            <RangePicker
              value={range}
              onChange={(v) => { setRange(v || [dayjs(), dayjs()]); setPage(1); }}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={5}>
            <Input
              placeholder="Buscar time..."
              value={customerNameInput}
              onChange={(e) => setCustomerNameInput(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} md={4}>
            <Input
              placeholder="Telefone..."
              value={cellphoneInput}
              onChange={(e) => setCellphoneInput(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} md={3}>
            <Select
              placeholder="Tipo"
              value={type}
              onChange={(v) => { setType(v); setPage(1); }}
              options={types.map((t) => ({ value: t, label: t }))}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} md={3}>
            <Select
              placeholder="Status"
              value={status}
              onChange={(v) => { setStatus(v); setPage(1); }}
              options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} md={2} style={{ textAlign: 'right' }}>
            <Button icon={<ReloadOutlined />} onClick={() => load()} title="Atualizar" />
          </Col>
        </Row>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        onRow={(record) => ({ onClick: () => openDetail(record) })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="Detalhes do envio"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={720}
      >
        {detailLoading || !detail ? (
          <Text>Carregando...</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text><b>Time:</b> {detail.customer_name || '-'}</Text>
            <Text><b>Jogador:</b> {detail.player_name || '-'}</Text>
            <Text><b>Telefone:</b> {detail.cellphone}</Text>
            <Text><b>Tipo:</b> {detail.type || '-'}</Text>
            <Text>
              <b>Status:</b>{' '}
              <Tag color={statusMeta(detail.status).color}>
                {statusMeta(detail.status).label}
              </Tag>
            </Text>
            <Text><b>Enviado em:</b> {detail.created_on ? dayjs(detail.created_on).format('DD/MM/YYYY HH:mm:ss') : '-'}</Text>
            <Text><b>Atualizado em:</b> {detail.status_updated_at ? dayjs(detail.status_updated_at).format('DD/MM/YYYY HH:mm:ss') : '-'}</Text>
            <Text><b>WAMID:</b> {detail.wamid || '-'}</Text>
            {detail.error_message && (
              <Text type="danger"><b>Erro:</b> {detail.error_code} — {detail.error_message}</Text>
            )}
            <Card size="small" title="Mensagem">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {detail.message_body || '(sem conteúdo registrado)'}
              </pre>
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default MessageLogs;
